import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, UserPlus, Trash2, Check, AlertCircle, Loader2, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPreRegisterPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [emails, setEmails] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!loading && (!isAuthenticated || user?.role !== "admin")) {
      navigate("/");
    }
  }, [isAuthenticated, user, loading, navigate]);

  useEffect(() => {
    fetchEmails();
  }, [token]);

  const fetchEmails = async () => {
    if (!token) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/pre-registered-emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch { /* silent */ }
    finally { setIsLoading(false); }
  };

  const handleSubmit = async () => {
    const emailList = inputText
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes("@"));

    if (emailList.length === 0) {
      setResult({ type: "error", message: "유효한 이메일이 없습니다." });
      return;
    }

    setIsSubmitting(true);
    setResult(null);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/pre-register-emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails: emailList, membership: "regular", auto_upgrade: true }),
      });
      const data = await res.json();
      if (res.ok) {
        const missingMsg = `${data.added}명 신규 사전등록 (가입 시 정회원 자동부여)`;
        const updatedMsg = data.updated ? ` · 기존 ${data.updated}건 갱신` : "";
        const memberMsg = data.already_members_count
          ? ` · 이미 가입된 회원 ${data.already_members_count}명(누락 아님)`
          : "";
        setResult({
          type: "success",
          message: missingMsg + updatedMsg + memberMsg,
          alreadyMembers: data.already_members || [],
        });
        setInputText("");
        fetchEmails();
      } else {
        setResult({ type: "error", message: data.detail || "등록에 실패했습니다." });
      }
    } catch {
      setResult({ type: "error", message: "서버 오류가 발생했습니다." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`${email}을(를) 삭제하시겠습니까?`)) return;
    try {
      const res = await safeFetch(`${API_URL}/api/admin/pre-registered-emails/${encodeURIComponent(email)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchEmails();
    } catch { /* silent */ }
  };

  const filtered = emails.filter(e => {
    if (filter === "used" && !e.used) return false;
    if (filter === "unused" && e.used) return false;
    if (searchQuery && !e.email.includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const usedCount = emails.filter(e => e.used).length;
  const unusedCount = emails.filter(e => !e.used).length;

  if (loading) return null;

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-preregister-page">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 hover:bg-stone-200 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">사전등록 정회원 관리</h1>
            <p className="text-sm text-stone-500">이메일을 등록하면 해당 이메일로 가입 시 자동으로 정회원(Regular) 부여</p>
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6" data-testid="preregister-input-section">
          <h2 className="font-bold text-stone-800 mb-3">이메일 일괄 등록</h2>
          <p className="text-xs text-stone-400 mb-3">이메일을 줄바꿈, 쉼표(,) 또는 세미콜론(;)으로 구분하여 입력하세요</p>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={"example1@gmail.com\nexample2@naver.com\nexample3@kakao.com"}
            className="w-full h-40 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 resize-none font-mono"
            data-testid="preregister-textarea"
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-stone-400">
              {inputText.split(/[\n,;]+/).filter(e => e.trim() && e.includes("@")).length}개 이메일 감지됨
            </span>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !inputText.trim()}
              className="btn-primary flex items-center gap-2"
              data-testid="preregister-submit-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              일괄 등록
            </Button>
          </div>
          {result && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${
              result.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`} data-testid="preregister-result">
              <div className="flex items-center gap-2">
                {result.type === "success" ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {result.message}
              </div>
              {result.alreadyMembers && result.alreadyMembers.length > 0 && (
                <details className="mt-2" data-testid="preregister-already-members">
                  <summary className="cursor-pointer text-xs text-emerald-600 font-medium">
                    이미 가입된 회원 {result.alreadyMembers.length}명 보기 (이미 데이터에 있음)
                  </summary>
                  <div className="mt-1.5 max-h-32 overflow-y-auto text-xs font-mono text-stone-500 space-y-0.5">
                    {result.alreadyMembers.map((em) => <div key={em}>{em}</div>)}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
            <p className="text-2xl font-bold text-stone-800">{emails.length}</p>
            <p className="text-xs text-stone-500">전체 등록</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
            <p className="text-2xl font-bold text-emerald-600">{usedCount}</p>
            <p className="text-xs text-stone-500">가입 완료</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200 text-center">
            <p className="text-2xl font-bold text-amber-600">{unusedCount}</p>
            <p className="text-xs text-stone-500">가입 대기</p>
          </div>
        </div>

        {/* Email List */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-stone-800">등록된 이메일 ({filtered.length})</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="이메일 검색"
                  className="pl-9 pr-3 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-burgundy-300"
                  data-testid="preregister-search"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none"
                data-testid="preregister-filter"
              >
                <option value="all">전체</option>
                <option value="unused">대기중</option>
                <option value="used">가입완료</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-stone-400 py-8">등록된 이메일이 없습니다</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filtered.map((item) => (
                <div
                  key={item.email}
                  className="flex items-center justify-between px-4 py-2.5 bg-stone-50 rounded-lg"
                  data-testid={`preregister-item-${item.email}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono text-stone-700">{item.email}</span>
                    {item.used ? (
                      <span className="text-[10px] font-medium bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full">가입완료</span>
                    ) : (
                      <span className="text-[10px] font-medium bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">대기중</span>
                    )}
                    <span className="text-[10px] text-stone-400">{item.membership}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(item.email)}
                    className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`preregister-delete-${item.email}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
